import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

interface DataPoint {
  label: string;
  value: number;
}

interface LineChartProps {
  data: DataPoint[];
  width?: number | string; // Accept string for percentage width
  height?: number | string;
  lineColor?: string;
  lineFillColor?: string;
}

const AreaLineChartTwo: React.FC<LineChartProps> = ({
  data,
  width = "100%", // Default to responsive width
  height = 200, // Set a default numeric height
  lineColor = "#ADE7CB",
  lineFillColor = "#EBF9F2",
  ...rest
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null); // Reference to the container
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    // Set up ResizeObserver to track container width
    const resizeObserver = new ResizeObserver(() => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      setContainerWidth(containerRef.current.clientWidth);
    }

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (containerWidth === 0) return; // Skip rendering until we have a valid width

    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove(); // Clear previous content

    const margin = { top: 5, left: 0, right: 0, bottom: 5 };
    const chartWidth = containerWidth - margin.left - margin.right; // Use container width
    const chartHeight = height as number;

    const x = d3
      .scaleLinear()
      .domain([0, data.length - 1])
      .range([0, chartWidth]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(data, (d: DataPoint) => d.value) || 1]) // Fallback for empty data
      .range([chartHeight, 0]);

    const line = d3
      .line<DataPoint>()
      .x((_, i) => x(i))
      .y((d) => y(d.value));

    const area = d3
      .area<DataPoint>()
      .x((_, i) => x(i))
      .y0(chartHeight)
      .y1((d) => y(d.value));

    const chart = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    chart
      .append("path")
      .datum(data)
      .attr("class", "line")
      .attr("d", line)
      .style("fill", "none")
      .style("stroke", lineColor)
      .style("stroke-width", 5);

    chart
      .append("path")
      .datum(data)
      .attr("class", "area")
      .attr("d", area)
      .style("fill", lineFillColor);

    chart.selectAll(".domain").remove();
    chart.selectAll(".tick line").remove();
  }, [data, height, lineColor, lineFillColor, containerWidth]);

  return (
    <div ref={containerRef} style={{ width: width, height: height }}>
      <svg ref={svgRef} width="100%" height="100%" {...rest}></svg>
    </div>
  );
};

export default AreaLineChartTwo;
